/**
 * Navigation slice — fetches and caches the app's menu tree (NavigationTree[]).
 * The tree drives the sidebar/top-nav and is refetched on BA or language change.
 *
 * Two fetch modes exist inside fetchNavigationTree:
 *  - Default (useParams omitted/false): NavigationService reads BA + language IDs
 *    from the Redux store internally, so callers don't need to pass them.
 *  - Explicit (useParams: true): caller passes baId/languageId; localStorage's
 *    selectedLanguageId takes precedence over the passed languageId to respect
 *    the user's last manual language override across page refreshes.
 */
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { NavigationTree } from '@/types/mms'
import NavigationService from '@/services/NavigationService'
import { RootState } from '@/store'

export interface NavigationState {
    navTree: NavigationTree[]
    loading: boolean
    error: string | null
}

const initialState: NavigationState = {
    navTree: [],
    loading: false,
    error: null,
}

interface FetchNavigationParams {
    languageId?: number
    baId?: number
    useParams?: boolean
}

export const fetchNavigationTree = createAsyncThunk(
    'navigation/fetchNavigationTree',
    async (
        params: FetchNavigationParams | undefined,
        { rejectWithValue, signal },
    ) => {
        try {
            let response

            const selectedLanguageId =
                localStorage.getItem('selectedLanguageId')

            // If specific parameters are provided and useParams flag is true, use them
            if (params?.useParams) {
                response = await NavigationService.getNavigationTreeWithParams(
                    selectedLanguageId
                        ? Number.parseInt(selectedLanguageId)
                        : params.languageId,
                    params.baId,
                    signal,
                )
            } else {
                // Otherwise use the default method that gets values from Redux
                response = await NavigationService.getNavigationTree(signal)
            }

            return response
        } catch (error) {
            // A stale request cancelled because BA/language changed mid-flight —
            // let it surface as an aborted thunk rather than a real fetch error.
            if (signal.aborted) {
                throw error
            }
            return rejectWithValue(
                error instanceof Error
                    ? error.message
                    : 'Failed to fetch navigation data',
            )
        }
    },
)

const navigationSlice = createSlice({
    name: 'navigation',
    initialState,
    reducers: {
        clearNavigationError: (state) => {
            state.error = null
        },
        setNavTree: (state, action: PayloadAction<NavigationTree[]>) => {
            state.navTree = action.payload
            state.loading = false
            state.error = null
        },
        resetNavigationState: (state) => {
            state.navTree = []
            state.loading = false
            state.error = null
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchNavigationTree.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(
                fetchNavigationTree.fulfilled,
                (state, action: PayloadAction<NavigationTree[]>) => {
                    state.navTree = action.payload
                    state.loading = false
                },
            )
            .addCase(fetchNavigationTree.rejected, (state, action) => {
                // Aborted requests (superseded by a newer BA/language fetch)
                // aren't real errors — the newer request owns loading/error state.
                if (action.meta.aborted) return
                state.loading = false
                state.error = action.payload as string
            })
    },
})

export const { clearNavigationError, setNavTree, resetNavigationState } =
    navigationSlice.actions

// Selectors
export const selectNavigation = (state: RootState) => state.navigation?.navTree ?? []
export const selectNavigationLoading = (state: RootState) =>
    state.navigation?.loading ?? false
export const selectNavigationError = (state: RootState) =>
    state.navigation?.error ?? null

export default navigationSlice.reducer
